import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Smartphone, CheckCircle2, XCircle, Send } from "lucide-react";

const Steps = ({ result }) => (
  <div data-testid="sms-test-result" className={`rounded-lg border p-3 ${result.ok ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}>
    <div data-testid="sms-test-verdict" className={`text-sm font-semibold mb-2 ${result.ok ? "text-emerald-800" : "text-red-800"}`}>
      {result.ok ? `Test text sent to ${result.phone}. Check your phone.` : "Texting isn't working yet"}
    </div>
    <ul className="space-y-1.5">
      {result.steps.map((s, i) => (
        <li key={s.label} data-testid={`sms-test-step-${i}`} className="flex items-start gap-2 text-sm">
          {s.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> : <XCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />}
          <span className="text-slate-700">{s.label}{s.detail ? <span className="block text-xs text-slate-500">{s.detail}</span> : null}</span>
        </li>
      ))}
    </ul>
  </div>
);

export const SmsTestButton = () => {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [result, setResult] = useState(null);
  const { data: last } = useQuery({ queryKey: ["sms-test"], enabled: open, queryFn: async () => (await api.get("/sms/test")).data });
  useEffect(() => { if (last?.last_phone && !phone) setPhone(last.last_phone); }, [last, phone]);
  const send = useMutation({
    mutationFn: async () => (await api.post("/sms/test", { phone })).data,
    onSuccess: (d) => { setResult(d); if (d.ok) toast.success("Test text sent"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Test failed"),
  });

  return (
    <>
      <Button data-testid="sms-test-open" variant="outline" size="sm" className="h-9" onClick={() => { setResult(null); setOpen(true); }}>
        <Smartphone className="h-4 w-4 mr-1.5" />Send me a test text
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="sms-test-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Send a test text</DialogTitle>
            <DialogDescription>Checks every step, from the pause switch to the texting service, then sends a short test text to your phone.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Input data-testid="sms-test-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Your mobile, e.g. (346) 555-0123" className="h-10" />
            <Button data-testid="sms-test-send" disabled={!phone.trim() || send.isPending} onClick={() => send.mutate()}
              className="h-10 bg-[#0B1F44] hover:bg-[#13306a] text-white shrink-0">
              <Send className="h-4 w-4 mr-1.5" />{send.isPending ? "Sending…" : "Send test"}
            </Button>
          </div>
          {result && <Steps result={result} />}
          <p className="text-[11px] text-slate-400">Up to 5 test texts per hour. Test texts aren't added to any patient's history.</p>
        </DialogContent>
      </Dialog>
    </>
  );
};
